import React, { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import Footer from './components/Footer';
import Header from './components/Header';
import TestCorsRequest from './components/TestCorsRequest';
import AdminLoginPage from './pages/AdminLoginPage';
import AppDetailPage from './pages/AppDetailPage';
import HomePage from './pages/HomePage';
import TestParamsPage from './pages/TestParamsPage';
import AdminAccountEditPage from './pages/admin/AdminAccountEditPage';
import AdminAccountsPage from './pages/admin/AdminAccountsPage';
import AdminAppCreatePage from './pages/admin/AdminAppCreatePage';
import AdminAppEditPage from './pages/admin/AdminAppEditPage';
import AdminAppVersionsPage from './pages/admin/AdminAppVersionsPage';
import AdminAppsPage from './pages/admin/AdminAppsPage';
import AdminForgotPasswordPage from './pages/admin/AdminForgotPasswordPage';
import AdminLayout from './pages/admin/AdminLayout';
import AdminResetPasswordPage from './pages/admin/AdminResetPasswordPage';
import AdminVerifyEmailPage from './pages/admin/AdminVerifyEmailPage';
import DownloadPage from './pages/admin/DownloadPage';
import { processUrlParams } from './utils/sessionUtils';
import { getSessionData } from './utils/storage';
import AdminLisenceManagementPage from './pages/admin/AdminLisenceManagementPage';
import AdminLisenceDetailPage from './pages/admin/AdminLisenceDetailPage';
import LoginRequiredModal from './components/LoginRequiredModal';

// Component to handle URL parameter processing and redirects
const UrlParamHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Check if we have token parameter in URL
    const hasProcessedParams = processUrlParams();

    // If parameters were processed, remove the token parameter from URL
    // but keep current path (no navigation to prevent redirect loops)
    if (hasProcessedParams && location.search.includes('token=')) {
      // Create a new URL without the token parameter
      const params = new URLSearchParams(location.search);
      params.delete('token');
      const newSearch = params.toString();
      const newPath = location.pathname + (newSearch ? `?${newSearch}` : '');

      // Use replace to avoid adding to history stack
      navigate(newPath, { replace: true });
    }

    // Mark that params have been checked and notify listeners
    try {
      localStorage.setItem('paramsChecked', 'true');
      window.dispatchEvent(new Event('params-processed'));
    } catch {}
  }, [location.pathname, location.search, navigate]);

  return null;
};

// Simple guard for admin dashboard based on localStorage token set by AdminLoginPage
const AdminProtected: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = (() => {
    try {
      return localStorage.getItem('admin-auth-token');
    } catch {
      return null;
    }
  })();
  const expiry = (() => {
    try {
      return localStorage.getItem('admin-auth-expiry');
    } catch {
      return null;
    }
  })();

  const isValid = token && expiry && Date.now() <= parseInt(expiry, 10);
  if (!isValid) {
    return <Navigate to='/admin/login' replace />;
  }
  return <>{children}</>;
};

// Router-aware content rendered inside BrowserRouter so we can use useLocation safely
const RouterContent: React.FC = () => {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isAdminLogin = location.pathname === '/admin/login';

  // Listen for params processing and toggle modal accordingly (only for non-admin routes)
  useEffect(() => {
    if (isAdminRoute) return; // do not show modal on admin

    const handleParamsProcessed = () => {
      const processed = localStorage.getItem('paramsProcessed') === 'true';
      setShowLoginModal(!processed);
    };

    // If UrlParamHandler already ran, evaluate immediately
    if (localStorage.getItem('paramsChecked') === 'true') {
      handleParamsProcessed();
    }

    window.addEventListener('params-processed', handleParamsProcessed);
    return () => window.removeEventListener('params-processed', handleParamsProcessed);
  }, [isAdminRoute]);

  // Lock scroll only when modal is visible
  useEffect(() => {
    document.body.style.overflow = !isAdminRoute && showLoginModal ? 'hidden' : '';
  }, [showLoginModal, isAdminRoute]);

  useEffect(() => {
    // Set document title
    document.title = isAdminRoute ? 'Admin - Takagi Portal' : 'Home - Takagi App Download';
  }, [isAdminRoute]);

  // Read agent session data from sessionStorage

  // After params processed, check session fields and show login-required modal if missing
  useEffect(() => {
    const { agentName, agentPersonInCharge, agencyEmail } = getSessionData();
    const missing = !agentName || !agentPersonInCharge || !agencyEmail;
    setShowLoginModal(missing);
  }, []);

  if (!isAdminRoute && showLoginModal) {
    return (
      <LoginRequiredModal
        isOpen={showLoginModal}
        force={true}
        loginUrl="https://www.takagi-global.com/en/members/"
        onClose={() => { /* forced modal, no-op */ }}
      />
    );
  }
  return (
    <div className='App' style={{ display: 'flex', minHeight: '100vh', flexDirection: 'column' }}>
      {!isAdminRoute && <UrlParamHandler />}
      {!isAdminRoute && <Header />}
      <div
        style={{ display: 'flex', justifyContent: 'center', margin: 0, padding: 0, width: '100%' }}
      >
        <Routes>
          <Route path='/' element={<HomePage />} />
          {/* Dynamic app detail route */}
          <Route path='/app/:id' element={<AppDetailPage />} />
          {/* Legacy route kept temporarily; redirect to home */}
          <Route path='/app/recorb' element={<Navigate to='/' replace />} />
          <Route path='/test-cors' element={<TestCorsRequest />} />
          <Route path='/test-params' element={<TestParamsPage />} />
          <Route path='/admin/login' element={<AdminLoginPage />} />
          <Route path='/admin/forgot-password' element={<AdminForgotPasswordPage />} />
          <Route path='/admin/reset-password' element={<AdminResetPasswordPage />} />
          {/* Public route for admin email verification */}
          <Route path='/admin/verify-email' element={<AdminVerifyEmailPage />} />
          <Route
            path='/admin/*'
            element={
              <AdminProtected>
                <AdminLayout />
              </AdminProtected>
            }
          >
            <Route index element={<AdminAppsPage />} />
            <Route path="apps" element={<AdminAppsPage />} />
            <Route path="apps/new" element={<AdminAppCreatePage />} />
            <Route path="apps/:id/edit" element={<AdminAppEditPage />} />
            <Route path="apps/:id/versions" element={<AdminAppVersionsPage />} />
            <Route path="account-edit" element={<AdminAccountEditPage />} />
            <Route path="accounts" element={<AdminAccountsPage />} />
            <Route path="license-management" element={<AdminLisenceManagementPage />} />
            <Route path="license-management/:id" element={<AdminLisenceDetailPage />} />
          </Route>
          <Route path='/admin/download/:id' element={<DownloadPage />} />
          <Route path='*' element={<Navigate to='/' replace />} />
        </Routes>
      </div>
      {(!isAdminRoute || isAdminLogin) && <Footer />}
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <RouterContent />
    </BrowserRouter>
  );
}

export default App;
